import React from 'react';
import { Box, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { FormDrawer } from '../../../../components/form-drawer';
import { dataGridDefaults } from '../../../../components/list-grid-panel';
import { EditAction, DeleteAction } from '../../../../components/row-actions';
import IrpItemForm from './irp-item-form';
import { useIrpItemController } from './controllers/irp-item-controller';
import { IIrpItemResponse } from '../../../../model/scp/irp/response/irp-item-response';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });

const TYPE_ITEM_LABEL: Record<string, string> = { M: 'MATERIAL', S: 'SERVIÇO' };

export interface IrpItemDataTableProps {
    irpId?: string;
    modalityId?: number | null;
    itemsIrpJson?: string;
    setItemsIrpJson: (value: string) => void;
}

export default function IrpItemDataTable({ irpId, modalityId, itemsIrpJson, setItemsIrpJson }: IrpItemDataTableProps) {
    const ctrl = useIrpItemController(irpId, modalityId, setItemsIrpJson, itemsIrpJson);

    const columns: GridColDef<IIrpItemResponse>[] = [
        { field: 'number', headerName: 'Nº', width: 80, sortable: false, disableColumnMenu: true, align: 'right', headerAlign: 'right' },
        {
            field: 'typeItemDescription', headerName: 'Tipo', flex: 1, minWidth: 120, sortable: false, disableColumnMenu: true,
            valueFormatter: ({ value }) => (typeof value === 'string' && TYPE_ITEM_LABEL[value.trim().toUpperCase()]) || value,
        },
        { field: 'description', headerName: 'Descrição', flex: 2, minWidth: 200, sortable: false, disableColumnMenu: true },
        { field: 'unitSupply', headerName: 'Unidade de fornecimento', flex: 1, minWidth: 180, sortable: false, disableColumnMenu: true },
        {
            field: 'unitValueEstimated', headerName: 'Valor estimado unitário', flex: 1, minWidth: 190, sortable: false, disableColumnMenu: true,
            align: 'right', headerAlign: 'right', valueFormatter: ({ value }) => currencyFormatter.format(Number(value)),
        },
        {
            field: 'actions', headerName: 'Ações', width: 100, sortable: false, disableColumnMenu: true,
            renderCell: (params) => (
                <React.Fragment>
                    <EditAction onClick={() => ctrl.editItem(params.row)} />
                    <DeleteAction onClick={() => ctrl.deleteItem(params.row)} />
                </React.Fragment>
            )
        }
    ];

    // Sem total no endpoint: a paginação no servidor busca uma página por vez; no rascunho pagina no navegador
    const paginationProps = ctrl.isDraft
        ? { initialState: { pagination: { paginationModel: { pageSize: 50, page: 0 } } } }
        : {
            paginationMode: 'server' as const,
            paginationModel: { page: ctrl.page, pageSize: ctrl.itemsPageSize },
            onPaginationModelChange: (model: { page: number }) => ctrl.handlePageChange(model.page),
            rowCount: ctrl.hasNextPage ? (ctrl.page + 1) * ctrl.itemsPageSize + 1 : ctrl.page * ctrl.itemsPageSize + ctrl.irpItems.length,
            pageSizeOptions: [ctrl.itemsPageSize],
        };

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1.5 }}>
                <Button variant="contained" startIcon={<PlaylistAddIcon />} onClick={ctrl.addItem}>
                    Adicionar item
                </Button>
            </Box>
            <DataGrid
                {...dataGridDefaults}
                columns={columns}
                rows={ctrl.irpItems}
                loading={ctrl.loading}
                getRowId={(row: IIrpItemResponse) => row.number ?? 0}
                {...paginationProps}
            />

            <FormDrawer
                open={ctrl.openDrawer}
                icon={<ListAltIcon />}
                title={ctrl.itemIrp.number !== undefined ? `Editar item nº ${ctrl.itemIrp.number}` : 'Adicionar item'}
                subtitle={ctrl.isRetification ? 'Retificação de item já publicado no PNCP' : undefined}
                saveLabel={ctrl.isRetification ? 'Retificar item' : 'Salvar item'}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.saveItem}
            >
                <IrpItemForm
                    itemIrp={ctrl.itemIrp}
                    materialService={ctrl.materialService}
                    categoryItemPca={ctrl.categoryItemPca}
                    judgementCriteria={ctrl.judgementCriteria}
                    isRetification={ctrl.isRetification}
                    errors={ctrl.validationErrors}
                    onChange={ctrl.handleOnChange}
                    onUnitValueChange={ctrl.handleUnitValueChange}
                />
            </FormDrawer>
        </React.Fragment>
    );
}
